<script>
import { mapGetters, mapActions } from 'vuex';
import { Form } from 'vee-validate';
import * as Yup from 'yup';

import FirstStep from '@/components/Booking/FirstStep';
import SecondStep from '@/components/Booking/SecondStep';
import SvgCheckSmall from '@/components/Svg/CheckSmall';

const initFormData = () => ({
  brand: null,
  model: null,
  service: null,
  date: null,
  time: null,
  dealer: null,
  name: null,
  checkbox: null,
  phone: null,
  comment: null,
});

const PLURAL_NAME = {
  brand: 'brands',
  service: 'services',
};

const STEPS = {
  brand: 1,
  model: 1,
  name: 1,
  phone: 1,
  dealer: 2,
  service: 2,
  date: 2,
  time: 2,
};

export default {
  name: 'BookingMain',
  components: {
    FirstStep,
    SecondStep,
    SvgCheckSmall,
    VeeForm: Form,
  },
  props: {
    formData: {
      type: Object,
      default: () => ({}),
    },
    defaultParams: {
      type: Object,
      default: () => ({}),
    },
  },
  initFormData,
  PLURAL_NAME,
  STEPS,
  data() {
    return {
      form: this.$options.initFormData(),
      currentStep: 1,
      errorMessage: null,
      loading: false,
    };
  },
  created() {
    for (const [key, value] of Object.entries(this.defaultParams)) {
      this.form[key] = this.formData[PLURAL_NAME[key]]?.find(
        item => item.id === value,
      );
    }
    this.setBrands(this.formData.brands);
    this.setModels(this.formData.models);
    this.setDealers(this.formData.dealers);
    this.setServices(this.formData.services);
  },
  setup() {
    const schemaFirstStep = Yup.object().shape({
      brand: Yup.mixed().nullable(false).required(),
      model: Yup.mixed().nullable(false).required(),
      name: Yup.string().required(),
      phone: Yup.string().required(),
    });

    const schemaFirstStepException = Yup.object().shape({
      brand: Yup.mixed().nullable(false).required(),
      name: Yup.string().required(),
      phone: Yup.string().required(),
    });

    const schemaSecondStep = Yup.object().shape({
      dealer: Yup.mixed().nullable(false).required(),
      service: Yup.mixed().nullable(false).required(),
      time: Yup.mixed().nullable(false).required(),
      comment: Yup.string().nullable(true).max(1500, 'Не больше 1500 символов'),
    });

    return {
      schemaFirstStep,
      schemaFirstStepException,
      schemaSecondStep,
    };
  },
  watch: {
    model() {
      this.form.model = this.model;
    },
    service() {
      this.form.service = this.service;
    },
  },
  computed: {
    ...mapGetters({
      model: 'Service/model',
      service: 'Service/service',
      recomendations: 'Service/recomendations',
    }),
  },
  methods: {
    ...mapActions({
      sendForm: 'Service/sendForm',
      setBrands: 'Service/setBrands',
      setModels: 'Service/setModels',
      setDealers: 'Service/setDealers',
      setServices: 'Service/setServices',
      loadRecomendations: 'Service/loadRecomendations',
    }),

    showError(message) {
      this.errorMessage = message;
    },
    onSubmit() {
      if (this.currentStep === 2) {
        if (!this.form.date) {
          this.showError('Выберите дату');
          return;
        } else if (!this.form.time) {
          this.showError('Выберите время');
          return;
        } else {
          this.errorMessage = null;
        }
      }

      if (this.currentStep === 2) {
        this.loading = true;
        const formData = {
          brandId: this.form.brand.id,
          brand: this.form.brand.name,
          model: this.form.model.name,
          service: Number(this.form.service.id),
          date: this.form.date.name,
          dealer: Number(this.form.dealer.id),
          name: this.form.name,
          phone: this.form.phone,
          custom_success_message: true,
          time: this.form.time.name,
          typeRepair: this.form.service.type_repair,
          comment: this.form.reccomend
            ? this.form.comment + this.form.reccomend
            : this.form.comment,
        };

        this.sendForm(formData).then(() => {
          this.loading = false;
          this.reset();
        });
        this.currentStep = 1;
      } else {
        this.loading = true;
        this.loadRecomendations({ telephone: this.form.phone }).then(() => {
          this.loading = false;
        });
        this.currentStep++;
      }
    },
    comeBack() {
      this.currentStep--;
    },
    updateForm(form) {
      this.form = Object.assign(this.form, form);
    },
    totalText(item) {
      return item.name || String(item).replace('_', '');
    },
    moveToStep(name) {
      this.currentStep = this.$options.STEPS[name];
    },
    resetFields() {
      this.form.model = null;
      this.form.service = null;
      this.form.dealer = null;
    },
    reset() {
      this.form = this.$options.initFormData();
    },
  },
};
</script>

<template>
  <div
    class="relative py-0 px-4 sm:py-4 sm:px-7 lg:py-14 lg:px-20 bg-white rounded-[20px]"
  >
    <span v-if="loading" class="loader"></span>

    <img
      src="/public/images/logo-opacity.png"
      class="absolute z-0 md:-top-5 lg:top-0 right-8 lg:right-4 lg:w-auto md:h-auto md:w-[131px] lg:h-auto"
      alt=""
    />
    <div
      class="relative max-w-[768px] lg:max-w-none mx-auto py-4 lg:py-0"
      :class="{ 'opacity-20': loading, 'pointer-events-none': loading }"
    >
      <div class="flex">
        <h2 class="text-2xl mb-6 text-gray-600">Записаться на сервис</h2>
        <div class="flex mb-6 items-center">
          <div class="flex items-center ml-8 mr-2.5">
            <div
              v-if="currentStep === 1"
              class="w-5 h-5 items-center justify-center flex bg-green rounded-3xl text-white"
            >
              1
            </div>
            <div
              v-else
              class="w-5 h-5 items-center justify-center flex bg-green rounded-3xl text-white fill-white"
            >
              <svg-check-small class="fill-white" />
            </div>
            <div class="ml-1.5 text-gray-600">шаг</div>
          </div>
          <div class="border-t border-gray-300 w-14 h-px"></div>
          <div class="flex items-center ml-2.5">
            <div
              class="w-5 h-5 items-center justify-center flex rounded-3xl"
              :class="
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400'
                  : 'bg-green text-white'
              "
            >
              2
            </div>
            <div
              class="ml-1.5"
              :class="currentStep === 1 ? 'text-gray-400' : 'text-gray-600'"
            >
              шаг
            </div>
          </div>
        </div>
      </div>
      <vee-form
        :validation-schema="
          currentStep === 1
            ? form.brand?.exception
              ? schemaFirstStepException
              : schemaFirstStep
            : currentStep === 2
              ? schemaSecondStep
              : null
        "
        novalidate
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-5"
        @submit="onSubmit"
      >
        <first-step
          v-if="currentStep === 1"
          :form="form"
          @update-form="updateForm"
          @changed-brand="resetFields"
        />
        <second-step
          v-else-if="currentStep === 2"
          :form="form"
          @update-form="updateForm"
        />

        <div
          class="relative col-auto sm:col-start-1 sm:col-end-2 lg:col-start-2 lg:col-end-3 text-gray-600"
        >
          <button type="submit" class="button button_green button_big w-full">
            {{ currentStep === 2 ? 'Записаться на сервис' : 'Далее >' }}
          </button>
          <div
            v-if="errorMessage"
            class="absolute top-[43px] left-0 text-red text-sm mt-1"
          >
            {{ errorMessage }}
          </div>
          <div
            v-if="currentStep !== 1"
            class="col-auto sm:col-start-1 sm:col-end-2 lg:col-start-2 lg:col-end-3 w-full text-base text-center mt-2 hover:text-black transition duration-200 ease-in-out cursor-pointer"
            @click="comeBack"
          >
            Назад
          </div>
        </div>
      </vee-form>

      <div class="flex flex-wrap gap-5 mt-3">
        <template v-for="(item, index) in form">
          <div
            v-if="item && index !== 'reccomend'"
            :key="index"
            v-html="totalText(item)"
            class="flex items-center justify-center px-4 py-1 text-gray-600 border border-white rounded-brand shadow-brand cursor-pointer hover:text-green transition duration-200 ease-in-out"
            @click="moveToStep(index)"
          ></div>
        </template>
      </div>
    </div>
  </div>
</template>

<style>
.loader {
  position: absolute;
  left: calc(50% - 24px);
  top: calc(50% - 24px);
  width: 48px;
  height: 48px;
  border: 5px solid #fff;
  border-bottom-color: rgb(140, 198, 63);
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
